<template>
  <v-page page="scoresOverview" title="Scores">
     <v-container class="px-0 mx-0 pt-1 mt-3">
      <v-layout>
        <v-flex>
          <v-autocomplete
            v-model="selectGroups"
            :items="scoreGroups"
            multiple
            chips
            outlined
            filled
            solo
            item-text="name"
            item-value="id"
            placeholder="Search Groups"
          >
            <template v-slot:selection="data">
              <v-chip
                :input-value="data.selected"
                close
                class="chip--select-multi"
                @input="remove(data.item)"
              >
              {{ data.item.name }}
              </v-chip>
            </template>
            <template v-slot:item="data">
                <v-list-item-content v-text="data.item.name"></v-list-item-content>
            </template>
          </v-autocomplete>
        </v-flex>
         <v-flex class="searchbar-btn" xs2 lg1 ml-2>
          <v-btn @click="search" color="primary">Search</v-btn>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-toolbar flat dense color="#FAFAFA" class="update-action">
            <v-spacer/>
            <v-csv-downloader
                  :disabled="searchGroups.length === 0"
                  :fields="csvFields"
                  :datasource="csvData"
                  name="scores"
                  buttonText="Download CSV"
                  customizable
                  class="d-flex pb-2"/>
          </v-toolbar>

          <v-data-table class="elevation-1 nowrap"
            :headers="tableHeaders"
            :items="scores"
            :options.sync="pagination"
            :server-items-length="pagination.totalItems"
            :loading=loading>
              <template v-slot:item="props">
                <tr>
                  <td>{{ props.item.id }}</td>
                  <td>{{ props.item.created_at | dateTime }}</td>
                  <td>{{ props.item.user.username }}</td>
                  <td>{{ props.item.assessment_group }}</td>
                  <td>{{ status(props.item) }}</td>
                  <td>{{ props.item.assessment_date | dateTime }}</td>
                  <td>{{ props.item.expires_at != props.item.end_date | booleanYesNo }}</td>
                  <td>{{ props.item.score != null && props.item.score.completed | booleanYesNo }}</td>
                  <td>{{ completedTasks(props.item) }}</td>
                  <td>{{ props.item.score != null ? props.item.score.overall_score : '' }}</td>
                  <td>{{ props.item.score != null ? props.item.score.global_quartile.name : '' }}</td>
                  <td>{{ props.item.score != null ? props.item.score.regional_quartile.name : '' }}</td>
                  <td style="border-left: 3px solid #eee">
                    <v-btn @click="$goUserDetails(props.item.user.id)" icon text>
                      <v-icon aria-hidden="false" role="button">zoom_in</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
           </v-data-table>
        </v-flex>
      </v-layout>
     </v-container>
  </v-page>
</template>

<script>
import { mapState } from 'vuex'
import VPage from '@/layouts/Default'
import RequestCancelToken from '@/mixins/RequestCancelToken'
import { Bus } from '@/bus'
import { searchAssessments, assessmentStatus } from '@/services/Accounts'
import { searchScores } from '@/services/Scores'
import keyBy from 'lodash.keyby'
import VCsvDownloader from '@/components/CsvDownloader/CsvDownloader.vue'

export default {
  name: 'ScoresOverview',
  components: {
    VPage,
    VCsvDownloader
  },
  mixins: [ RequestCancelToken ],
  data () {
    return {
      scores: [],
      selectGroups: [],
      searchGroups: [],
      loading: false,
      tableHeaders: [
        { text: 'Assessment Id', value: 'id', sortable: false },
        { text: 'Created', value: 'created_at', sortable: false },
        { text: 'Username', value: 'username', sortable: false },
        { text: 'Assessment Group', value: 'assessment_group', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Starting Window', value: 'assessment_date' },
        { text: 'Started', value: 'notset', sortable: false, $can: { task: 'read' } },
        { text: 'Completed', value: 'notset', sortable: false },
        { text: 'Completed Tasks', value: 'completed_tasks', sortable: false, width: '10' },
        { text: 'Score', value: 'overall_score', sortable: false },
        { text: 'Global Quartile', value: 'global_quartile', sortable: false },
        { text: 'Regional Quartile', value: 'regional_quartile', sortable: false },
        { text: 'User Details', value: 'id', sortable: false }
      ],
      pagination: {
        descending: true,
        rowsPerPage: 10,
        page: 1,
        totalItems: 0,
        sortBy: ['id']
      },
      csvFields: {
        id: 'id',
        created_at: { 'attribute': 'created_at', formatter: 'date' },
        username: 'user.username',
        assessment_group: 'assessment_group',
        scheduled: { 'attribute': 'assessment_date', formatter: 'date' },
        status: this.csvFormatter,
        started: this.csvFormatter,
        completed: this.csvFormatter,
        completed_tasks: this.csvFormatter,
        score: 'score.overall_score',
        global_quartile: 'score.global_quartile.name',
        regional_quartile: 'score.regional_quartile.name'
      }
    }
  },
  watch: {
    pagination (newValue, oldValue) {
      this.search()
    }
  },
  computed: {
    ...mapState('scores', {
      scoreGroupMap: 'groups'
    }),
    scoreGroups () {
      return Object.values(this.scoreGroupMap)
    },
    /**
     * The list of query parameter
     */
    searchParameters () {
      if (this.searchGroups.length === 0) {
        throw Error('No groups selected')
      }

      const query = {
        limit: this.pagination.rowsPerPage,
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        order: (this.pagination.descending ? '-' : '+') + this.pagination.sortBy,
        assessment_groups: this.searchGroups.map((id) => this.scoreGroupMap[id].name).join(',')
      }

      return query
    }
  },
  methods: {
    search () {
      if (this.selectGroups.length === 0) {
        return
      }
      this.searchGroups = this.selectGroups

      const results = []
      let totalResults = 0
      this.$set(this, 'loading', true)
      searchAssessments(this.searchParameters, this.requestToken.token)
        .then(({ assessments, total }) => {
          this.requestCancelTokenClearer()
          results.push(...assessments)
          totalResults += total

          if (total === 0) {
            return []
          }

          return searchScores({ user_assessment_ids: results.map((assessment) => assessment.id) })
        })
        .then((scores) => {
          this.mergeScores(results, scores.scores)
          this.scores = results
          this.pagination.totalItems = totalResults
          this.$set(this, 'loading', false)
        })
        .catch((error) => {
          if (error && error.message !== this.requestCancelMessage) {
            this.$set(this, 'loading', false)
            this.requestCancelTokenClearer()
            Bus.$emit('alert', ({ color: 'error', message: error }))
          }
        })
    },
    mergeScores (assessments, scores) {
      const scoreLookup = keyBy(scores, 'user_assessment_id')
      for (let assessment of assessments) {
        assessment.score = scoreLookup[assessment.id] || null
      }
    },
    completedTasks (assessment) {
      if (!assessment.score) {
        return ''
      }
      return assessment.score.tasks.filter((task) => task.completed).map((task) => task.task_short_name).join(', ')
    },
    status (assessment) {
      if (assessment.score === null) {
        return assessmentStatus(assessment)
      }
      return 'Scored'
    },
    remove (item) {
      const index = this.selectGroups.indexOf(item.id)
      if (index >= 0) this.selectGroups.splice(index, 1)
    },
    csvData () {
      return Promise.all([
        searchAssessments({ assessment_groups: this.searchGroups.map((id) => this.scoreGroupMap[id].name).join(','), limit: -1 }),
        searchScores({ score_group_ids: this.searchGroups.join(','), limit: -1 })
      ])
        .then(([assessments, scores]) => {
          const result = assessments.assessments
          this.mergeScores(result, scores.scores)
          return result
        })
    },
    csvFormatter (key, row) {
      if (key === 'status') {
        return assessmentStatus(row)
      }

      if (key === 'started') {
        return row.expires_at !== row.end_date
      }

      if (key === 'completed') {
        return row.score ? 'Yes' : 'No'
      }

      if (key === 'completed_tasks') {
        let tasks = row.score && row.score.tasks ? row.score.tasks : []
        return tasks.filter((task) => task.completed).map((task) => task.task_short_name).join(', ')
      }
    }
  }
}
</script>

<style lang="sass">
.nowrap td
  white-space: nowrap

.update-action
  .v-toolbar__content
    padding: 0 !important

  .v-btn
    margin-right: 0 !important

.searchbar-btn button
  margin-top: 0 !important
  height: 50px
  min-height:50px
  width: 94%

</style>
